(function () {
function isMobileSlider() {
    return window.matchMedia('(max-width: 768px)').matches;
}

function isTabletSlider() {
    return window.matchMedia('(max-width: 1200px)').matches;
}

function getTestimonialsBreakpoint() {
    if (isMobileSlider()) {
        return 'mobile';
    }

    if (isTabletSlider()) {
        return 'tablet';
    }

    return 'desktop';
}

function getMobileSliderPadding() {
    return {
        left: 0,
        right: 0,
    };
}

function getTestimonialsSliderPadding() {
    if (isMobileSlider()) {
        return getMobileSliderPadding();
    }

    const styles = getComputedStyle(document.documentElement);
    const containerPadding = parseFloat(styles.getPropertyValue('--container-padding')) || 20;
    const containerMax = 1400;
    const bleed = 166;
    const minOffset = 18;
    const viewportWidth = window.innerWidth;
    const containerOffset = Math.max(containerPadding, (viewportWidth - containerMax) / 2 + containerPadding);
    const left = Math.max(minOffset, containerOffset - bleed);

    return {
        left: `${left}px`,
        right: '20px',
    };
}

function getSplideOptions() {
    if (isMobileSlider()) {
        return {
            type: 'slide',
            perPage: 1,
            perMove: 1,
            fixedWidth: undefined,
            fixedHeight: undefined,
            autoHeight: true,
            gap: 0,
            padding: getMobileSliderPadding(),
            pagination: false,
            arrows: false,
            drag: true,
            snap: true,
            focus: 0,
            trimSpace: true,
            updateOnMove: true,
        };
    }

    return {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        fixedWidth: isTabletSlider() ? '440px' : '513px',
        fixedHeight: undefined,
        autoHeight: false,
        gap: '11px',
        padding: getTestimonialsSliderPadding(),
        pagination: false,
        arrows: false,
        drag: true,
        snap: true,
        focus: 0,
        trimSpace: false,
        updateOnMove: true,
    };
}

function removeSplidePagination(root) {
    root.querySelectorAll('.splide__pagination').forEach((pagination) => {
        pagination.remove();
    });
}

function resetTestimonialsSliderDom(root) {
    const list = root.querySelector('.splide__list');
    const track = root.querySelector('.splide__track');

    root.querySelectorAll('.splide__slide').forEach((slide) => {
        slide.removeAttribute('style');
        slide.classList.remove('is-active', 'is-visible', 'is-prev', 'is-next');
    });

    list?.removeAttribute('style');
    track?.removeAttribute('style');
    root.classList.remove('is-initialized', 'is-rendered', 'is-active');
}

function initHomeTestimonialsSlider(root) {
    if (typeof Splide === 'undefined') {
        return;
    }

    const section = root.closest('.home-testimonials');
    const prevBtn = section?.querySelector('[data-testimonials-prev]');
    const nextBtn = section?.querySelector('[data-testimonials-next]');
    const dots = section?.querySelectorAll('[data-testimonials-dot]') ?? [];
    let splide = null;
    let breakpoint = getTestimonialsBreakpoint();
    let resizeTimer = null;

    const updateDots = (index) => {
        dots.forEach((dot) => {
            const dotIndex = Number(dot.getAttribute('data-testimonials-dot'));
            const isActive = dotIndex === index;

            dot.classList.toggle('is-active', isActive);
            dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
    };

    const updateNavButtons = () => {
        if (!splide || !prevBtn || !nextBtn || !isMobileSlider()) {
            prevBtn?.removeAttribute('disabled');
            nextBtn?.removeAttribute('disabled');
            return;
        }

        const end = splide.Components.Controller.getEnd();
        prevBtn.disabled = splide.index <= 0;
        nextBtn.disabled = splide.index >= end;
    };

    const bindDotControls = () => {
        dots.forEach((dot) => {
            if (dot.dataset.testimonialsBound === 'true') {
                return;
            }

            dot.addEventListener('click', () => {
                const index = Number(dot.getAttribute('data-testimonials-dot'));
                splide?.go(index);
            });
            dot.dataset.testimonialsBound = 'true';
        });
    };

    const bindNavControls = () => {
        if (!prevBtn || !nextBtn || prevBtn.dataset.testimonialsBound === 'true') {
            return;
        }

        prevBtn.addEventListener('click', () => {
            splide?.go('<');
        });
        nextBtn.addEventListener('click', () => {
            splide?.go('>');
        });

        prevBtn.dataset.testimonialsBound = 'true';
        nextBtn.dataset.testimonialsBound = 'true';
    };

    const mountSlider = ({ preserveIndex = false } = {}) => {
        const targetIndex = preserveIndex && splide ? splide.index : 0;

        if (splide) {
            try {
                splide.destroy(true);
            } catch (error) {
                // Ignore destroy race during rapid resize.
            }
            splide = null;
        }

        resetTestimonialsSliderDom(root);
        removeSplidePagination(root);

        splide = new Splide(root, getSplideOptions());

        splide.on('mounted', () => {
            removeSplidePagination(root);

            if (preserveIndex && targetIndex > 0) {
                splide.go(Math.min(targetIndex, splide.Components.Controller.getEnd()));
            }

            updateDots(splide.index);
            updateNavButtons();
        });

        splide.on('moved', (index) => {
            updateDots(index);
            updateNavButtons();
        });

        splide.on('resize', updateNavButtons);

        splide.mount();

        // Second pass after layout settles (DevTools / CSS media sync).
        window.requestAnimationFrame(() => {
            if (!splide) {
                return;
            }

            splide.refresh();
            updateDots(splide.index);
            updateNavButtons();
        });
    };

    bindNavControls();
    bindDotControls();
    mountSlider();

    const handleResize = () => {
        window.clearTimeout(resizeTimer);
        resizeTimer = window.setTimeout(() => {
            const nextBreakpoint = getTestimonialsBreakpoint();

            // Always remount on breakpoint change OR mobile↔any —
            // refresh() alone leaves broken fixedWidth / autoHeight state.
            if (nextBreakpoint !== breakpoint) {
                breakpoint = nextBreakpoint;
                mountSlider({ preserveIndex: true });
                return;
            }

            // Same breakpoint: remount so padding/widths recalculate cleanly.
            mountSlider({ preserveIndex: true });
        }, 150);
    };

    window.addEventListener('resize', handleResize);

    // matchMedia fires more reliably than resize for CSS breakpoint sync.
    const mobileMq = window.matchMedia('(max-width: 768px)');
    const tabletMq = window.matchMedia('(max-width: 1200px)');
    const onMediaChange = () => handleResize();

    if (typeof mobileMq.addEventListener === 'function') {
        mobileMq.addEventListener('change', onMediaChange);
        tabletMq.addEventListener('change', onMediaChange);
    } else {
        mobileMq.addListener(onMediaChange);
        tabletMq.addListener(onMediaChange);
    }
}

document.querySelectorAll('[data-home-testimonials-slider]').forEach(initHomeTestimonialsSlider);

})();
